import { useCallback, useEffect, useState } from "react";
import { Control, Path, useFormContext, useWatch } from "react-hook-form";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { orange } from "@mui/material/colors";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import {
  add,
  addDays,
  differenceInCalendarDays,
  intervalToDuration,
  isBefore,
  isValid
} from "date-fns";

import CheckboxFormField from "components/checkbox-form-field";
import TimePicker from "components/time-picker";
import { EVENT_TYPE_APPOINTMENT_BLOCK } from "constants/appointments";
import useReservedAppointmentTypes from "features/scheduling/scheduler/use-reserved-appointment-types.hook";
import { AppointmentBlock } from "services/types/events.type";
import { getValidDateOrDefault, strToDuration } from "utils/common";

import { AppointmentFormValues } from "../../appointment-form.component";
import useShowPickers from "../../use-show-pickers.hook";
import AppointmentDateField from "./appointment-date-field";

type AppointmentDatesFieldProps = {
  label?: string;
  showAvailableWarning?: boolean;
  showOverlapWarning?: boolean;
  defaultValues: {
    appointment_date: Date;
    start_time: Date;
    end_time: Date;
  };
  control: Control<AppointmentFormValues, object>;
  setValue: (
    name: Path<AppointmentFormValues>,
    value: any,
    options?: any
  ) => void;
  disabled?: boolean;
  fromLabel?: string;
  toLabel?: string;
};

export default function AppointmentDatesField({
  defaultValues,
  control,
  setValue,
  showAvailableWarning = false,
  showOverlapWarning = false,
  label,
  disabled = false,
  fromLabel,
  toLabel
}: AppointmentDatesFieldProps) {
  const [hasInitialDuration, setHasInitialDuration] = useState<boolean>(false);

  const {
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext();

  const {
    setOpenStartDate,
    openStartTime,
    setOpenStartTime,
    openEndTime,
    setOpenEndTime
  } = useShowPickers();

  const closeAllPickers = useCallback(() => {
    setOpenStartDate(false);
    setOpenStartTime(false);
    setOpenEndTime(false);
  }, [setOpenStartDate, setOpenStartTime, setOpenEndTime]);

  const showStartTimePicker = useCallback(() => {
    setOpenStartDate(false);
    setOpenStartTime(true);
    setOpenEndTime(false);
  }, [setOpenStartDate, setOpenStartTime, setOpenEndTime]);

  const showEndTimePicker = useCallback(() => {
    setOpenStartDate(false);
    setOpenStartTime(false);
    setOpenEndTime(true);
  }, [setOpenStartDate, setOpenStartTime, setOpenEndTime]);

  const appointmentType = useWatch({ control, name: "appointment_type" });
  const shouldCreateScheduleForStaff = useWatch({
    control,
    name: "should_create_schedule_for_staff"
  });

  const [
    eventType,
    eventId,
    overwriteBlock,
    appointmentDate,
    startTime,
    endTime
  ]: [string, number | null | undefined, boolean | undefined, any, any, any] =
    useWatch({
      control,
      name: [
        "event_type",
        "id",
        "overwrite_block",
        "appointment_date",
        "start_time",
        "end_time"
      ]
    });

  const [appointmentDuration, setAppointmentDuration] = useState<Duration>({
    hours: 1,
    minutes: 0,
    seconds: 0
  });
  const { isReserved, intersectingBlocks } = useReservedAppointmentTypes();

  const validateTimeDifference = useCallback(() => {
    if (!isBefore(startTime, endTime)) {
      setError("timeDifferenceError", {
        type: "custom"
      });
    } else {
      clearErrors("timeDifferenceError");
    }
  }, [clearErrors, endTime, setError, startTime]);

  useEffect(() => {
    validateTimeDifference();
  }, [validateTimeDifference]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (hasInitialDuration && appointmentType?.default_duration) {
      const appointmentTypeDuration = strToDuration(
        appointmentType.default_duration
      );

      setValue(
        "end_time",
        add(
          getValidDateOrDefault(startTime, defaultValues.start_time),
          appointmentTypeDuration
        )
      );
    }
  }, [appointmentType?.default_duration]);

  useEffect(() => {
    if (hasInitialDuration) {
      setValue(
        "start_time",
        addDays(
          startTime as Date,
          differenceInCalendarDays(
            getValidDateOrDefault(
              appointmentDate,
              defaultValues.appointment_date
            ),
            startTime as Date
          )
        )
      );

      setValue(
        "end_time",
        addDays(
          endTime as Date,
          differenceInCalendarDays(
            getValidDateOrDefault(
              appointmentDate,
              defaultValues.appointment_date
            ),
            endTime as Date
          )
        )
      );
    }
  }, [appointmentDate?.toString()]);

  useEffect(() => {
    if (hasInitialDuration) {
      const durationToUse = appointmentType?.default_duration
        ? strToDuration(appointmentType.default_duration)
        : appointmentDuration;
      setValue(
        "end_time",
        add(
          getValidDateOrDefault(startTime, defaultValues.start_time),
          durationToUse
        )
      );
    }
  }, [startTime?.toString(), appointmentType?.default_duration]);

  useEffect(() => {
    try {
      if (
        hasInitialDuration &&
        isValid(startTime) &&
        isValid(endTime) &&
        Number(endTime) > Number(startTime)
      ) {
        setAppointmentDuration(
          intervalToDuration({ start: startTime as Date, end: endTime as Date })
        );
      }
    } catch (err) {
      if (isValid(startTime)) {
        setValue("end_time", add(startTime as Date, appointmentDuration));
      }

      // TODO: Handle duration error as form error
      console.log("Handle error duration error on the form", err);
    }
  }, [endTime?.toString()]);

  useEffect(() => {
    if (
      isValid(startTime) &&
      isValid(endTime) &&
      Number(endTime) > Number(startTime)
    ) {
      setAppointmentDuration(
        intervalToDuration({ start: startTime as Date, end: endTime as Date })
      );
      setHasInitialDuration(true);
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <>
      <ClickAwayListener onClickAway={closeAllPickers}>
        <Stack direction="row" pl="36px" gap={2} alignItems="end">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <AppointmentDateField
              name="appointment_date"
              control={control}
              label={label}
              disabled={disabled}
              hidePicker={closeAllPickers}
            />
            <TimePicker
              name="start_time"
              control={control as any}
              openPicker={openStartTime}
              hidePicker={closeAllPickers}
              showPicker={showStartTimePicker}
              disabled={disabled}
              label={fromLabel}
            />
            <TimePicker
              name="end_time"
              control={control as any}
              openPicker={openEndTime}
              hidePicker={closeAllPickers}
              showPicker={showEndTimePicker}
              disabled={disabled}
              label={toLabel}
            />
          </LocalizationProvider>
        </Stack>
      </ClickAwayListener>

      {showOverlapWarning && (
        <Stack direction="row">
          <Box sx={{ pl: 4, pt: 1.5, color: orange[900] }}>
            <Stack direction="row" spacing={0.25}>
              <WarningAmberIcon sx={{ height: 18 }} />
              <Typography>
                This appointment intersects with existing ones
              </Typography>
            </Stack>
          </Box>
          <FormControlLabel
            label="Allow Overlap"
            sx={{ pl: 3 }}
            control={
              <CheckboxFormField
                testId="allow_overlap"
                name="allow_overlap"
                control={control}
              />
            }
          />
        </Stack>
      )}

      {Boolean(showAvailableWarning && !shouldCreateScheduleForStaff) && (
        <Box sx={{ pl: 4, color: orange[900], pt: 1.5 }}>
          <Stack direction="row" spacing={0.25}>
            <WarningAmberIcon sx={{ height: 18 }} />
            <Typography>Appointment is outside provider's schedule</Typography>
          </Stack>
        </Box>
      )}

      {Boolean(
        eventType !== EVENT_TYPE_APPOINTMENT_BLOCK &&
          isReserved &&
          !overwriteBlock
      ) && (
        <Box sx={{ pl: 4, color: orange[900] }}>
          <Stack direction="row" spacing={0.25}>
            <WarningAmberIcon sx={{ height: 18 }} />
            <Typography>
              Appointment intersects with reserved time block
              {Boolean(intersectingBlocks?.at(0)?.appointmentType?.name)
                ? `: ${intersectingBlocks?.at(0)?.appointmentType?.name}`
                : ""}
            </Typography>
          </Stack>
        </Box>
      )}

      {Boolean(
        eventType === EVENT_TYPE_APPOINTMENT_BLOCK &&
          intersectingBlocks?.length &&
          intersectingBlocks.every(
            (appointment: AppointmentBlock) => appointment.id !== eventId
          )
      ) && (
        <Stack direction="row">
          <Box sx={{ pl: 4, pt: 1.5, color: orange[900] }}>
            <Stack direction="row" spacing={0.25}>
              <WarningAmberIcon sx={{ height: 18 }} />
              <Typography>
                Appointment block already exists
                {Boolean(intersectingBlocks?.at(0)?.appointmentType?.name)
                  ? `: ${intersectingBlocks?.at(0)?.appointmentType?.name}`
                  : ""}
              </Typography>
            </Stack>
          </Box>
          <FormControlLabel
            label="Allow Overlapping Blocks"
            sx={{ pl: 3 }}
            control={
              <CheckboxFormField
                testId="allow_overlapping_blocks"
                name="allow_overlapping_blocks"
                control={control}
              />
            }
          />
        </Stack>
      )}

      {errors.timeDifferenceError && (
        <Box sx={{ pl: 4, color: orange[900] }}>
          <Stack direction="row" spacing={0.25}>
            <WarningAmberIcon sx={{ height: 18 }} />
            <Typography>End time is before start time</Typography>
          </Stack>
        </Box>
      )}
    </>
  );
}
